'use client';

import React, { useState, useMemo } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { CaptchaDialog as Dialog, CaptchaDialogContent as DialogContent } from '@/components/SmartCaptchaDialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Phone, CheckCircle2, AlertCircle } from 'lucide-react';
import { useLanguage } from '@/lib/config/useLanguage';
import { getBranchData, getBranchCodeFromPath } from '@/lib/config/branches-config';
import { buildLeadThankYouUrl, clearPendingLeadSubmissionId, extractOptionalLeadId, getOrCreatePendingLeadSubmissionId } from '@/lib/forms/lead-thank-you';
import { useSmartCaptcha } from '@/components/SmartCaptcha';

const FORM_TYPE = 'category-price-request';

export default function CategoryPriceActions() {
  const pathname = usePathname() || '/';
  const router = useRouter();
  const { localizePath } = useLanguage();
  const [isPriceModalOpen, setIsPriceModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: '',
    privacy: false,
    marketing: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [submitMessage, setSubmitMessage] = useState('');
  const { captchaElement, executeCaptcha, resetCaptcha, isCaptchaReady, challengeVisible } = useSmartCaptcha();

  // Получаем данные текущего филиала для телефона на основе пути
  const currentBranch = useMemo(() => {
    const branchCode = getBranchCodeFromPath(pathname);
    return getBranchData(branchCode);
  }, [pathname]);
  
  const phoneNumber = currentBranch?.phone || '+7 (812) 333-33-33';

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (name: string, checked: boolean) => {
    setFormData((prev) => ({ ...prev, [name]: checked }));
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Проверка обязательных полей
    if (!formData.privacy) {
      setSubmitStatus('error');
      setSubmitMessage('Необходимо согласие на обработку персональных данных');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');
    setSubmitMessage('');

    try {
      const submissionId = getOrCreatePendingLeadSubmissionId(FORM_TYPE, window.location.pathname);
      const smartToken = await executeCaptcha();
      const response = await fetch('/api/forms', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          message: formData.message,
          privacy: formData.privacy,
          marketing: formData.marketing,
          formType: FORM_TYPE,
          pageUrl: typeof window !== 'undefined' ? window.location.href : '',
          smartToken,
          submissionId,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        const thankYouUrl = buildLeadThankYouUrl({
          pathname: window.location.pathname,
          search: window.location.search,
          formType: FORM_TYPE,
          leadId: extractOptionalLeadId(data),
          sid: submissionId,
        });

        clearPendingLeadSubmissionId(FORM_TYPE, window.location.pathname);
        router.push(thankYouUrl);
        return;
      } else {
        setSubmitStatus('error');
        setSubmitMessage(data.message || 'Ошибка при отправке заявки');
      }
    } catch (error) {
      setSubmitStatus('error');
      setSubmitMessage('Произошла ошибка. Попробуйте позже.');
      console.error('Ошибка при отправке формы:', error);
    } finally {
      resetCaptcha();
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Card className="w-full border border-slate-200 shadow">
        <CardHeader>
          <CardTitle>Свяжитесь с нами для бесплатной консультации и расчёта стоимости</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col lg:flex-row gap-3">
            <Button
              type="button"
              variant="default"
              size="default"
              onClick={() => setIsPriceModalOpen(true)}
              className="w-full lg:flex-1 bg-slate-900 text-slate-50 hover:bg-slate-900/90"
            >
              УЗНАТЬ ЦЕНУ
            </Button>
            
            <Button
              type="button"
              variant="outline"
              size="default"
              onClick={() => window.location.href = `tel:${phoneNumber.replace(/\s/g, '')}`}
              className="w-full lg:flex-1"
            >
              <Phone className="h-4 w-4" />
              ПОЗВОНИТЬ
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Модальное окно запроса цены */}
      <Dialog challengeVisible={challengeVisible} busy={isSubmitting} open={isPriceModalOpen} onOpenChange={setIsPriceModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Запрос расчёта стоимости</DialogTitle>
            <DialogDescription>
              Заполните форму, и мы свяжемся с вами в ближайшее время для расчёта стоимости монтажа/ремонта.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleFormSubmit} className="mt-4 space-y-4">
            <div>
              <Label htmlFor="category-price-name" className="text-sm font-medium text-gray-700">
                ФИО <span className="text-red-500">*</span>
              </Label>
              <Input
                id="category-price-name"
                name="name"
                type="text"
                required
                value={formData.name}
                onChange={handleFormChange}
                className="mt-2"
                placeholder="Иванов Иван Иванович"
              />
            </div>
            <div>
              <Label htmlFor="category-price-phone" className="text-sm font-medium text-gray-700">
                Телефон <span className="text-red-500">*</span>
              </Label>
              <Input
                id="category-price-phone"
                name="phone"
                type="tel"
                required
                value={formData.phone}
                onChange={handleFormChange}
                className="mt-2"
                placeholder="+7 (999) 123-45-67"
              />
            </div>

            <div>
              <Label htmlFor="category-price-email" className="text-sm font-medium text-gray-700">
                Email <span className="text-red-500">*</span>
              </Label>
              <Input
                id="category-price-email"
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleFormChange}
                className="mt-2"
                placeholder="example@mail.com"
              />
            </div>

            <div>
              <Label htmlFor="category-price-message" className="text-sm font-medium text-gray-700">
                Дополнительная информация
              </Label>
              <textarea
                id="category-price-message"
                name="message"
                value={formData.message}
                onChange={handleFormChange}
                className="mt-2 flex min-h-[100px] w-full rounded-md border border-slate-200 bg-transparent px-3 py-2 text-base shadow-sm transition-colors placeholder:text-slate-500 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-slate-950 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm dark:border-slate-800 dark:placeholder:text-slate-400 dark:focus-visible:ring-slate-300"
                placeholder="Опишите детали монтажа/ремонта, укажите размеры и другие важные параметры"
                rows={4}
              />
            </div>

            {/* Чекбоксы согласия */}
            <div className="space-y-3">
              <div className="flex items-start">
                <Checkbox
                  id="category-price-privacy"
                  checked={formData.privacy}
                  onCheckedChange={(checked) => handleCheckboxChange('privacy', checked as boolean)}
                  required
                  className="mt-1"
                />
                <Label htmlFor="category-price-privacy" className="ml-2 text-sm text-gray-600">
                  Я даю согласие на обработку своих персональных данных и соглашаюсь с{' '}
                  <a href={localizePath('/pages/politika-konfidentsialnosti-personalnykh-dannykh')} className="text-blue-600 hover:underline">
                    политикой конфиденциальности
                  </a>
                  . <span className="text-red-500">*</span>
                </Label>
              </div>

              <div className="flex items-start">
                <Checkbox
                  id="category-price-marketing"
                  checked={formData.marketing}
                  onCheckedChange={(checked) => handleCheckboxChange('marketing', checked as boolean)}
                  className="mt-1"
                />
                <Label htmlFor="category-price-marketing" className="ml-2 text-sm text-gray-600">
                  Я даю согласие на направление рекламных рассылок.
                </Label>
              </div>
            </div>

            {/* Сообщения о результате */}
            {submitStatus === 'success' && (
              <div className="flex items-center gap-2 rounded-lg bg-green-50 p-4 text-green-800">
                <CheckCircle2 className="h-5 w-5 shrink-0" />
                <p className="text-sm font-medium">{submitMessage}</p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="flex items-center gap-2 rounded-lg bg-red-50 p-4 text-red-800">
                <AlertCircle className="h-5 w-5 shrink-0" />
                <p className="text-sm font-medium">{submitMessage}</p>
              </div>
            )}

            {captchaElement}

            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsPriceModalOpen(false);
                  setSubmitStatus('idle');
                  setSubmitMessage('');
                }}
                disabled={isSubmitting}
              >
                Отмена
              </Button>
              <Button
                type="submit"
                variant="default"
                className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                disabled={!isCaptchaReady || isSubmitting || !formData.privacy}
              >
                {isSubmitting ? 'Отправка...' : 'Отправить заявку'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
